import { useEffect, useState } from "react";
import { EyeIcon, FilterIcon2, VendingActiveIcon } from "../../../../assets/icons";
import { useDebounce } from "../../../../hooks/useDebounce";
import { formatTimestamp } from "../../../../utils";
import { startOfDay, nextDayStart } from "../../../../utils/date-params";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { DateRangeFilter } from "../../../../common/components/xepp/date-range-filter/DateRangeFilter";

import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";

import styles from "../../customer.module.scss";
import { AdminCustomerModel, CustomerSearchByFilter, CustomerTypeEnum } from "../../../../model/customer/customer";
import { useExportCustomersForUtilityAdminMutation, useLazyGetCustomerForUtilityAdminQuery } from "../../../../service/customer-service";
import { CustomerMenuActiveIcon } from "../../../../assets/sidemenu-icons";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { hasPermission } from "../../../../guards/common/authentification-functions";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import { toast } from "react-toastify";

interface CustomerListPageProps {
	onCustomerSelect: (customer: AdminCustomerModel) => void;
}

export const CustomerListPage = ({ onCustomerSelect }: CustomerListPageProps) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [searchBy, setSearchBy] = useState<CustomerSearchByFilter>(CustomerSearchByFilter.CustomerName);
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [isOpen, setIsOpen] = useState(false);
	const [dateFrom, setDateFrom] = useState<Date | null>(null);
	const [dateTo, setDateTo] = useState<Date | null>(null);
	const [page, setPage] = useState(1);
	const pageSize = 15;

	const { updateLayout } = useConfigLayout();
	const [getCustomerForUtilityAdmin, { data, isLoading, isFetching }] = useLazyGetCustomerForUtilityAdminQuery();
	const [exportCustomers, { isLoading: isExporting }] = useExportCustomersForUtilityAdminMutation();

	const { count } = data ?? {};

	// The API treats From as inclusive and To as exclusive.
	const dateParams = {
		...(dateFrom && { From: startOfDay(dateFrom).toISOString() }),
		...(dateTo && { To: nextDayStart(dateTo).toISOString() }),
	};

	useEffect(() => {
		const queryParams = {
			page,
			pageSize,
			searchBy,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
			...dateParams,
		};

		getCustomerForUtilityAdmin(queryParams);
	}, [page, pageSize, debouncedSearchTerm, searchBy, dateFrom, dateTo, getCustomerForUtilityAdmin]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleApplyDateFilter = (from: Date | null, to: Date | null) => {
		setDateFrom(from);
		setDateTo(to);
		setPage(1);
	};

	const formatDateRange = () => {
		if (!dateFrom || !dateTo) return "Date range";

		const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
		return `${dateFrom.toLocaleDateString("en-US", options)} - ${dateTo.toLocaleDateString("en-US", options)}`;
	};

	const handleExport = async () => {
		if (!hasPermission(UtilityPermissions.ViewCustomerTable)) {
			toast.warning("You do not have the permission for this action");
			return;
		}

		if (!count) {
			toast.error("There are no customers matching the current filters to export");
			return;
		}

		try {
			await exportCustomers({
				searchBy,
				...(debouncedSearchTerm && { search: debouncedSearchTerm }),
				...dateParams,
			}).unwrap();
			toast.success("Your export is being prepared and will be sent to your email");
		} catch {
			toast.error("Could not start the export. Please try again");
		}
	};

	useEffect(() => {
		updateLayout({ showExportButton: true, exportHandler: isExporting ? () => {} : handleExport });

		return () => updateLayout({ showExportButton: false, exportHandler: null });
	}, [debouncedSearchTerm, searchBy, dateFrom, dateTo, count, isExporting]);

	const handleViewCustomer = (customer: AdminCustomerModel) => {
		if (hasPermission(UtilityPermissions.ViewCustomerTable)) onCustomerSelect(customer);
		else toast.warning("You do not have the permission for this action");
	};

	const columns: Array<ColumnType<AdminCustomerModel>> = [
		{
			accessor: "name",
			header: "Name of Customer",
			render: (record: AdminCustomerModel) => <span className={styles.customerName}>{`${record.firstName} ${record.lastName}`}</span>,
		},
		{
			accessor: "customerType",
			header: "Customer Type",
			render: (record: AdminCustomerModel) => (
				<span className={styles.ellipsisText}>{`${record.customerType === CustomerTypeEnum.Individual ? "Individual" : "Corporate"}`}</span>
			),
		},
		{
			accessor: "email",
			header: "Email",
			render: (record: AdminCustomerModel) => (
				<span title={record.username} className={styles.ellipsisText}>
					{record.username}
				</span>
			),
		},
		{
			accessor: "phoneNumber",
			header: "Phone Number",
			render: (record: AdminCustomerModel) => <span className={styles.phoneNumber}>{record.phoneNumber}</span>,
		},
		{
			accessor: "meterNumber",
			header: "Meter Number",
			render: (record: AdminCustomerModel) => <span className={styles.meterNumber}>{record.meterNumber}</span>,
		},
		{
			accessor: "accountNumber",
			header: "Account Number",
			render: (record: AdminCustomerModel) => <span className={styles.accountNumber}>{record.accountNumber}</span>,
		},
		// {
		// 	accessor: "district",
		// 	header: "District",
		// 	render: (record: AdminCustomerModel) => (
		// 		<span className={styles.district}>
		// 			{record.district}
		// 		</span>
		// 	),
		// },
		{
			accessor: "date",
			header: "Date Joined",
			render: (record: AdminCustomerModel) => <span className={styles.dateJoined}>{formatTimestamp(record.dateJoined)}</span>,
		},
		{
			accessor: "id",
			header: "",
			render: (record: AdminCustomerModel) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => handleViewCustomer(record)} />
			),
		},
	];

	const searchByOptions = [
		new CommonSelectItem(CustomerSearchByFilter.CustomerName, "Customer Name"),
		new CommonSelectItem(CustomerSearchByFilter.Username, "Email"),
		new CommonSelectItem(CustomerSearchByFilter.PhoneNumber, "Phone Number"),
		new CommonSelectItem(CustomerSearchByFilter.MeterNumberOrAccountNumber, "Meter / Account Number"),
	];

	const handleSearchByChange = (item: CommonSelectItem | undefined) => {
		if (!item) return;
		setSearchBy(item.key as CustomerSearchByFilter);
		setPage(1);
	};

	// Header actions component
	const headerActions = (
		<div className={styles.searchByRow}>
			<div className={styles.searchBySelect}>
				<CommonSelect
					items={searchByOptions}
					selected={searchByOptions.find(option => option.key === searchBy)}
					placeholder="Search by"
					onChange={handleSearchByChange}
					searchable={false}
				/>
			</div>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search customers..." />
			<Button
				variant="outline"
				className={styles.dateRangeButton}
				leftIcon={<FilterIcon2 style={{ marginRight: 3 }} />}
				text={formatDateRange()}
				onClick={() => setIsOpen(true)}
			/>
		</div>
	);

	return (
		<div className={styles.historyContainer}>
			<DateRangeFilter isOpen={isOpen} onClose={() => setIsOpen(false)} onApply={handleApplyDateFilter} initialFrom={dateFrom} initialTo={dateTo} />
			<DataTable<AdminCustomerModel>
				data={data?.items ?? []}
				loading={isLoading}
				isUpdating={isFetching}
				columns={columns}
				headerActions={headerActions}
				emptyComponent={<EmptyState icon={<CustomerMenuActiveIcon />} title="No customers found" />}
			/>
			<TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={pageSize} currentPage={page} onPageChange={setPage} />
		</div>
	);
};
